import React, { useState, useEffect, useRef } from 'react';
import { Menu, Search, Bell, ChevronDown, Store, Check, Loader } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const Topbar = ({ onSidebarToggle }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    
    const axiosSecure = UseAxiosSecure();
    
    // --- 0. GET USER ROLE ---
    const { user, activeBranchId, setActiveBranchId } = UseAuth();
    const userRole = user?.role || 'user'; // Extract role to lock the dropdown
    console.log("Logged in user data:", user);
    console.log("User role:", userRole);

    // --- 1. FETCH BRANCHES ---
    const { data: branches = [], isLoading } = useQuery({
        queryKey: ['branches-list'],
        queryFn: async () => {
            const res = await axiosSecure.get('/branches');
            return res.data?.data || [];
        }
    });

  // --- 2. AUTO-SELECT CORRECT BRANCH ---
    useEffect(() => {
        if (!activeBranchId && branches.length > 0) {
            if (userRole === 'staff' && user?.branchId) {
                // If they are staff, force the app to load their specific assigned branch
                setActiveBranchId(user.branchId); 
            } else {
                // If admin/owner, load the first branch in the list normally
                setActiveBranchId(branches[0]._id);
            }
        }
    }, [branches, activeBranchId, setActiveBranchId, userRole, user]);

    // --- 3. CLICK OUTSIDE TO CLOSE DROPDOWN ---
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Get current branch details for the button label
    const activeBranch = branches.find(b => b._id === activeBranchId);

    // --- 4. HANDLE BRANCH SWITCH ---
    const handleBranchChange = (branchId) => {
        setActiveBranchId(branchId);
        setIsDropdownOpen(false);
        // Optional: Force a window reload to ensure all components refetch with new branch ID instantly
        window.location.reload(); 
    };

    return (
        <header className="h-20 bg-[#0f172a]/80 backdrop-blur-xl border-b border-white/5 flex items-center justify-between px-4 md:px-8 sticky top-0 z-40">
            
            <div className="flex items-center gap-4">
                {/* Mobile Sidebar Toggle */}
                <button 
                    onClick={onSidebarToggle}
                    className="md:hidden p-2 text-slate-400 hover:text-white bg-white/5 rounded-lg transition-colors"
                >
                    <Menu size={24} />
                </button>

                {/* --- PREMIUM BRANCH SWITCHER --- */}
                {userRole === 'staff' ? (
                    
                    // 🔒 STAFF VIEW: Locked assigned location badge
                    <div 
                        className="flex items-center gap-3 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl cursor-not-allowed opacity-80"
                        title="Staff cannot change assigned location"
                    >
                        <div className="p-1.5 bg-slate-800 border border-white/10 rounded-lg shadow-lg">
                            <Store size={16} className="text-pink-500" />
                        </div>
                        
                        <div className="flex flex-col items-start hidden sm:flex">
                            <span className="text-[10px] font-bold text-pink-500 uppercase tracking-widest leading-none mb-1">Assigned Location</span>
                            {isLoading ? (
                                <div className="h-4 w-24 bg-white/10 rounded animate-pulse"></div>
                            ) : (
                                <span className="text-sm font-bold text-slate-300 leading-none">
                                    {activeBranch ? activeBranch.name : 'Loading...'}
                                </span>
                            )}
                        </div>
                    </div>

                ) : (
                    
                    // 🔓 ADMIN/OWNER VIEW: Full interactive dropdown
                    <div className="relative" ref={dropdownRef}>
                        <button 
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            disabled={isLoading || branches.length === 0}
                            className="flex items-center gap-3 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all disabled:opacity-50"
                        >
                            <div className="p-1.5 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg shadow-lg">
                                <Store size={16} className="text-white" />
                            </div>
                            
                            <div className="flex flex-col items-start hidden sm:flex">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Active Location</span>
                                {isLoading ? (
                                    <div className="h-4 w-24 bg-white/10 rounded animate-pulse"></div>
                                ) : (
                                    <span className="text-sm font-bold text-white leading-none">
                                        {activeBranch ? activeBranch.name : 'No Branches Yet'}
                                    </span>
                                )}
                            </div>
                            
                            <ChevronDown 
                                size={16} 
                                className={`text-slate-400 ml-2 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} 
                            />
                        </button>

                        {/* Dropdown Menu */}
                        {isDropdownOpen && (
                            <div className="absolute top-full left-0 mt-2 w-64 bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="p-2">
                                    {branches.map((branch) => (
                                        <button
                                            key={branch._id}
                                            onClick={() => handleBranchChange(branch._id)}
                                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                                                activeBranchId === branch._id 
                                                    ? 'bg-gradient-to-r from-pink-500/10 to-purple-500/10 text-pink-400 border border-pink-500/20' 
                                                    : 'text-slate-300 hover:bg-white/5 hover:text-white border border-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <Store size={16} className={activeBranchId === branch._id ? 'text-pink-500' : 'text-slate-500'} />
                                                <span className="text-left">{branch.name}</span>
                                            </div>
                                            {activeBranchId === branch._id && <Check size={16} className="text-pink-500" />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Search Bar (Hidden on small mobile) */}
            <div className="hidden md:flex items-center bg-[#1e293b] rounded-full px-4 py-2 border border-white/5 focus-within:border-pink-500/50 focus-within:shadow-[0_0_15px_rgba(236,72,153,0.1)] transition-all w-96 max-w-md mx-4">
                <Search size={18} className="text-slate-500" />
                <input 
                    type="text" 
                    placeholder="Search flavors, orders, users..." 
                    className="bg-transparent border-none outline-none text-sm text-white placeholder-slate-500 ml-3 w-full"
                />
            </div>

            {/* Right Actions: Notification & Profile */}
            <div className="flex items-center gap-4">
                <button className="relative p-2 text-slate-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full">
                    <Bell size={20} />
                    <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-pink-500 rounded-full border-2 border-[#0f172a] animate-pulse"></span>
                </button>
                
                {/* Profile Avatar */}
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 border-2 border-white/10 flex items-center justify-center shadow-lg cursor-pointer hover:scale-105 transition-transform">
                    <span className="text-white font-bold text-sm">SA</span>
                </div>
            </div>
        </header>
    );
};

export default Topbar;